import React, { useState } from 'react';
import { connect } from 'react-redux';
import SettingsPageLayout from './SettingsPageLayout';
import { useOnboarding } from '../onboarding/ImportJobContext';
import { IS_MOCK_API } from '../onboarding/onboarding.service';
import { FIRST_TASKS, taskState } from '../onboarding/firstTaskRegistry';
import toast from '../shared/Toast';
import { resetIntroDone } from '../onboarding/bailey-intro/introStage';
import { SETUP_V2_ENABLED } from '../onboarding/flags';
import { useSetup, openSetup } from '../onboarding/setup/SetupContext';
import { OWNER_CHAPTERS } from '../onboarding/setup/setupPlan';
import './SettingsModern.css';
import './OnboardingSettings.css';

/**
 * Settings → Onboarding (staging only, see ONBOARDING_DEMO_VISIBLE).
 *
 * Lets us demo the first-login setup on a salon that already exists:
 * restart it exactly as a new signup sees it, reopen it where it left
 * off, jump to a screen, or put the diary checklist back. Nothing here
 * touches clients, bookings or money; it only moves the onboarding state.
 */

const STATUS = {
  new: { label: 'Not started', tone: 'default', blurb: 'This salon has never opened setup. A new signup lands here.' },
  in_progress: { label: 'In progress', tone: 'info', blurb: 'Setup was opened and not finished. The diary shows the checklist.' },
  dismissed: { label: 'Dismissed', tone: 'default', blurb: 'The owner chose to set things up themselves.' },
  completed: { label: 'Completed', tone: 'ok', blurb: 'Setup reached the reveal and bookings went live.' },
};
// Set up with Bailey has no "Try it" tasks (they were the first-run card's),
// so they'd never tick there.
const CHECKLIST = [['starter', 'Starter menu or import'], ...(SETUP_V2_ENABLED ? [] : FIRST_TASKS.map((t) => [t.key, `Try it: ${t.title.toLowerCase()}`])), ['live', 'Take bookings live']];
const rowDone = (checklist, key) => {
  if (key === 'starter') return checklist.starter === 'done' || checklist.import === 'done';
  if (key === 'live') return checklist.live === 'done';
  return taskState(checklist)[key] === 'done';
};

const clearMock = () => { try { sessionStorage.removeItem('beu_onboarding_mock_v1'); } catch (e) { /* storage unavailable */ } };

// The checklist merges key by key on the server, so an empty checklist
// clears nothing. Set Bailey's own keys back explicitly.
// Any practice tasks left from the old rehearsal go too.
const V2_RESET = { setup: null, welcomeDone: false, tasks: {} };

function OnboardingSettings(props) {
  const ob = useOnboarding();
  const setup = useSetup();
  const [busy, setBusy] = useState(false);
  const state = ob?.onboarding || { status: 'new', checklist: {} };
  const meta = STATUS[state.status] || STATUS.new;
  const checklist = state.checklist || {};
  const done = CHECKLIST.filter(([k]) => rowDone(checklist, k)).length;

  // The honest restart: wipe the state, then arrive on the diary the way a
  // fresh signup does (?setup=1), full reload included.
  const restart = async () => {
    setBusy(true);
    if (IS_MOCK_API) clearMock();
    resetIntroDone();
    if (SETUP_V2_ENABLED && setup) setup.reset();
    await ob.patchOnboarding({ status: 'new', activeJobId: null, checklist: SETUP_V2_ENABLED ? V2_RESET : {} });
    const salonId = localStorage.getItem('salonId') || '';
    window.location.assign(`/diary/${salonId}?setup=1`);
  };
  const setStatus = async (status, extra = {}) => {
    await ob.patchOnboarding({ status, ...extra });
    toast.success(`Onboarding marked ${STATUS[status].label.toLowerCase()}`);
  };

  return (
    <SettingsPageLayout activeTab="onboarding" {...props}>
      <div className="catalogue-content-wrapper obs-page">
        <div className="obs-banner">
          <b>Staging tool.</b> This page is hidden on customer builds. Use it to demo the setup flow new salons see when they first sign in.
        </div>

        <div className="settings-card">
          <div className="settings-card-header obs-card-head">
            <div>
              <h2>Where you are</h2>
              <p>{meta.blurb}</p>
            </div>
            <span className={`settings-badge settings-badge-${meta.tone === 'ok' ? 'default obs-badge-ok' : meta.tone === 'info' ? 'info' : 'default'}`}>{meta.label}</span>
          </div>
          <div className="obs-grid">
            <div className="obs-fact"><small>Checklist</small><b>{done} of {CHECKLIST.length} done{checklist.hidden ? ', card hidden' : ''}</b></div>
            <div className="obs-fact"><small>Data source</small><b>{IS_MOCK_API ? 'Scripted demo' : 'Live API'}</b></div>
            <div className="obs-fact"><small>Active import</small><b>{state.activeJobId ? 'Yes' : 'None'}</b></div>
            {!SETUP_V2_ENABLED && <div className="obs-fact"><small>Practice data</small><b>{!checklist.practice || !checklist.practice.clientId ? 'None' : checklist.practice.removed ? 'Removed' : checklist.practice.paid ? 'Booking paid' : 'In the diary'}</b></div>}
          </div>
          <ul className="obs-checklist">
            {CHECKLIST.map(([k, label]) => (
              <li key={k} className={rowDone(checklist, k) ? 'is-done' : ''}>
                <span className="obs-tick" aria-hidden="true">
                  <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
                </span>
                {label}
              </li>
            ))}
          </ul>
        </div>

        <div className="settings-card">
          <div className="settings-card-header">
            <h2>Replay</h2>
            <p>Restart plays the whole thing from the first screen, exactly as a new signup sees it. {SETUP_V2_ENABLED ? 'The starter menu writes real services to this salon.' : 'The starter menu writes real services, a sample client and a practice booking to this salon.'} Services that already exist by name are skipped.</p>
          </div>
          <div className="obs-actions">
            <button type="button" className="settings-btn settings-btn-primary" disabled={busy} onClick={restart}>
              {busy ? 'Restarting…' : 'Restart from the beginning'}
            </button>
            <button type="button" className="settings-btn settings-btn-secondary" onClick={() => (SETUP_V2_ENABLED ? openSetup({}) : ob.openLayer({ mode: 'setup' }))}>Open where it left off</button>
          </div>
          {SETUP_V2_ENABLED ? (
          <div className="obs-jump">
            <span className="obs-jump-label">Jump to</span>
            <button type="button" className="settings-btn-link" onClick={() => window.location.assign(`/diary/${localStorage.getItem('salonId') || ''}?intro=1`)}>Bailey's welcome</button>
            {OWNER_CHAPTERS.map((c) => (
              <button key={c.id} type="button" className="settings-btn-link" onClick={() => openSetup({ chapter: c.id })}>{c.title}</button>
            ))}
            <button type="button" className="settings-btn-link" onClick={() => ob.openLayer({ mode: 'import' })}>Bring more in</button>
          </div>
          ) : (
          <div className="obs-jump">
            <span className="obs-jump-label">Jump to</span>
            <button type="button" className="settings-btn-link" onClick={() => window.location.assign(`/diary/${localStorage.getItem('salonId') || ''}?intro=1`)}>Bailey's welcome</button>
            <button type="button" className="settings-btn-link" onClick={() => ob.openLayer({ mode: 'setup', screen: 'start' })}>Welcome</button>
            <button type="button" className="settings-btn-link" onClick={() => ob.openLayer({ mode: 'setup', screen: 'menu' })}>Starter menu</button>
            <button type="button" className="settings-btn-link" onClick={() => ob.openLayer({ mode: 'setup', screen: 'import' })}>Bring your files</button>
            <button type="button" className="settings-btn-link" onClick={() => ob.openLayer({ mode: 'setup', screen: 'ex' })}>Needs you</button>
            <button type="button" className="settings-btn-link" onClick={() => ob.openLayer({ mode: 'setup', screen: 'reveal' })}>The reveal</button>
            <button type="button" className="settings-btn-link" onClick={async () => { await ob.patchOnboarding({ status: 'in_progress', checklist: { hidden: false, tasks: {}, live: 'todo' } }); props.history.push(`/diary/${localStorage.getItem('salonId') || ''}`); }}>First tasks on the diary</button>
            <button type="button" className="settings-btn-link" onClick={() => ob.openLayer({ mode: 'setup', screen: 'live' })}>Go live</button>
            <button type="button" className="settings-btn-link" onClick={() => ob.openLayer({ mode: 'import' })}>Bring more in</button>
          </div>
          )}
        </div>

        <div className="settings-card">
          <div className="settings-card-header">
            <h2>Set the state by hand</h2>
            <p>Useful for showing what an existing salon sees at each stage.</p>
          </div>
          <div className="obs-actions obs-actions--wrap">
            <button type="button" className="settings-btn settings-btn-secondary" onClick={() => setStatus('in_progress', { checklist: { hidden: false } })}>Show the diary card</button>
            <button type="button" className="settings-btn settings-btn-secondary" onClick={() => setStatus('completed')}>Mark completed</button>
            <button type="button" className="settings-btn settings-btn-secondary" onClick={() => setStatus('dismissed')}>Mark dismissed</button>
            <button type="button" className="settings-btn settings-btn-secondary" onClick={async () => { if (IS_MOCK_API) clearMock(); if (SETUP_V2_ENABLED && setup) setup.reset(); resetIntroDone(); await ob.patchOnboarding({ status: 'new', activeJobId: null, checklist: SETUP_V2_ENABLED ? V2_RESET : {} }); toast.success('Onboarding reset'); }}>Reset without opening</button>
          </div>
        </div>

        {IS_MOCK_API && (
          <div className="settings-card">
            <div className="settings-card-header">
              <h2>Demo files</h2>
              <p>The scripted demo reads file names, not contents. Drop any file with one of these words in its name to follow the story.</p>
            </div>
            <ul className="obs-files">
              <li><code>clients.csv</code> <span>Fresha export, 1,240 clients, one duplicate pair to merge</span></li>
              <li><code>price-list.pdf</code> <span>48 services at your prices, two with no price to check</span></li>
              <li><code>appointments.xlsx</code> <span>6 staff with rotas and upcoming bookings</span></li>
            </ul>
          </div>
        )}
      </div>
    </SettingsPageLayout>
  );
}

export default connect((state) => ({ user: state.user }))(OnboardingSettings);
